/**
 * Descargas de Nukli.
 *
 * Los binarios los compila GitHub Actions (m-ai-l/.github/workflows/release.yml)
 * y su job `publish-downloads` los sube por rsync a nukliapp.com/downloads/, que
 * es de donde los sirve esta web (mismo dominio, sin depender de terceros).
 *
 * Cada build tiene un nombre ESTABLE (`Nukli-latest-…`) que en el servidor es un
 * enlace simbólico al fichero de la versión actual. Por eso los enlaces de esta
 * página nunca cambian y funcionan aunque no se pueda leer nada más.
 *
 * `latest.json`, que el mismo job genera junto a los binarios, solo añade el
 * número de versión y el tamaño de cada fichero para las etiquetas. Si no se
 * puede leer, los botones siguen descargando: solo se quedan sin el "128 MB".
 */

/** Base de las descargas. En producción es el mismo origen que esta página. */
export const DOWNLOADS_URL = (
  process.env.VUE_APP_DOWNLOADS_URL || 'https://nukliapp.com/downloads'
).replace(/\/$/, '');

/** App web: mismo cliente que el de escritorio, en el navegador. */
export const WEB_APP_URL = process.env.VUE_APP_WEB_APP_URL || 'https://mail.nukliapp.com';

/** Versión de reserva mientras no se ha leído `latest.json`. */
export const APP_VERSION = process.env.VUE_APP_APP_VERSION || '';

/**
 * Un binario descargable.
 *
 * `file` es el nombre estable en el servidor y debe coincidir con el campo
 * `stable` de BUILDS en m-ai-l/scripts/build-downloads-manifest.mjs; `id`, con
 * las claves de `latest.json`.
 *
 * `os` agrupa las variantes de un mismo sistema: la página muestra primero el
 * grupo del sistema detectado y el resto debajo.
 */
export const BUILDS = [
  {
    id: 'mac-arm',
    os: 'mac',
    primary: true,
    labelKey: 'download.builds.macArm',
    ext: 'DMG',
    icon: 'apple',
    file: 'Nukli-latest-mac-arm64.dmg',
  },
  {
    id: 'mac-intel',
    os: 'mac',
    labelKey: 'download.builds.macIntel',
    ext: 'DMG',
    icon: 'apple',
    file: 'Nukli-latest-mac-x64.dmg',
  },
  {
    id: 'win',
    os: 'windows',
    primary: true,
    labelKey: 'download.builds.win',
    ext: 'EXE',
    icon: 'monitor',
    file: 'Nukli-latest-win-x64-setup.exe',
  },
  {
    id: 'win-portable',
    os: 'windows',
    labelKey: 'download.builds.winPortable',
    ext: 'EXE',
    icon: 'monitor',
    file: 'Nukli-latest-win-x64-portable.exe',
  },
  {
    id: 'linux-appimage',
    os: 'linux',
    primary: true,
    labelKey: 'download.builds.linuxAppImage',
    ext: 'APPIMAGE',
    icon: 'terminal',
    file: 'Nukli-latest-linux-x86_64.AppImage',
  },
  {
    id: 'linux-deb',
    os: 'linux',
    labelKey: 'download.builds.linuxDeb',
    ext: 'DEB',
    icon: 'terminal',
    file: 'Nukli-latest-linux-amd64.deb',
  },
  {
    id: 'linux-rpm',
    os: 'linux',
    labelKey: 'download.builds.linuxRpm',
    ext: 'RPM',
    icon: 'terminal',
    file: 'Nukli-latest-linux-x86_64.rpm',
  },
];

/** URL de descarga de un build. Siempre válida: no depende del manifiesto. */
export function downloadUrl(build) {
  return `${DOWNLOADS_URL}/${build.file}`;
}

/** Sistema operativo del visitante, para destacar su descarga. */
export function detectOS() {
  if (typeof navigator === 'undefined') return 'mac';
  const ua = `${navigator.userAgent} ${navigator.platform || ''}`;
  // iOS/Android primero: contienen "Mac"/"Linux" en el UA y darían falso positivo.
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return 'mobile';
  if (/Win/i.test(ua)) return 'windows';
  if (/Mac/i.test(ua)) return 'mac';
  if (/Linux|X11/i.test(ua)) return 'linux';
  return 'mac';
}

/**
 * ¿Mac con Apple Silicon? El user agent miente (Safari y Chrome dicen "Intel
 * Mac OS X" en ambos), así que lo deducimos del renderer de WebGL, que sí
 * expone el chip. Ante la duda devolvemos `true`: hoy la mayoría de Macs son
 * Apple Silicon, y el `.dmg` de Intel al menos arranca ahí bajo Rosetta
 * mientras que al revés no.
 */
export function isAppleSilicon() {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return true;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (!info) return true;
    const renderer = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) || '');
    return !/Intel/i.test(renderer);
  } catch (err) {
    return true;
  }
}

/**
 * Lee `latest.json`: `{ version, files: { [buildId]: { name, size } } }`.
 * Devuelve `null` si no se puede (los enlaces funcionan igual).
 */
export async function fetchLatestRelease() {
  try {
    const res = await fetch(`${DOWNLOADS_URL}/latest.json`, { cache: 'no-cache' });
    if (!res.ok) return null;
    const json = await res.json();
    return json && json.files ? json : null;
  } catch (err) {
    return null;
  }
}

/** Tamaño legible para la etiqueta del botón (p. ej. "128 MB"). */
export function formatSize(bytes) {
  if (!bytes) return '';
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}
